import { inject, Injectable } from '@angular/core';
import { DocumentEntity } from '@domain';
import { EMPTY, map, Observable, switchMap } from 'rxjs';
import { DocumentSectionController } from 'src/app/domain/controllers/document/document-section.controller';
import { EditDataService } from './_edit-data.service';
import { FieldRemovalService } from './field-removal.service';

@Injectable({
    providedIn: 'root',
})
export class EditSectionService {
    // * [Injects]
    private _editDataService = inject(EditDataService);
    private _documentSessionController = inject(DocumentSectionController);
    private _fieldRemovalService = inject(FieldRemovalService);

    /**
     * Cria uma seção pela API e atualiza via pusher
     * @param documentId ID of document
     */
    public createSession(documentId: string): Observable<DocumentEntity> {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        return this._documentSessionController.createSection(documentId);
    }

    /**
     * Exclui uma seção
     * @param sectionId ID of section
     */
    public removeSession(sectionId: string): void {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return;

        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return;

        // Remover a seção que contém um campo em janela de desfazer deixaria o
        // commit pendente órfão (seção inexistente no servidor).
        this._fieldRemovalService
            .flush()
            .pipe(
                switchMap(() =>
                    this._documentSessionController.deleteSection(documentId, sectionId)
                )
            )
            .subscribe({
                next: () => {
                    // Ativa a primeira seção como a seção atual
                    if (this._editDataService.document()?.sessions.length > 0) {
                        this._editDataService.setCurrentSession(
                            this._editDataService.document()?.sessions[0]
                        );
                    }
                },
            });
    }

    /**
     * Altera o nome da seção
     * @param sectionId ID of section
     * @param name Name of section
     */
    public changeNameSession(sectionId: string, name: string): Observable<void> {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        const document = this._editDataService.document();
        if (!document) return EMPTY;

        // Busca a seção
        const section = document.sessions.find(x => x._id == sectionId);
        if (!section) return EMPTY;

        // Remove espaços em branco do nome
        const newName = name.trim();

        // Se o nome não mudou ou está vazio, não faz nada
        if (!newName || newName === section.name) return EMPTY;

        // Atualiza o nome da seção
        return this._documentSessionController
            .updateSectionName(document._id, sectionId, newName)
            .pipe(
                map(() => {
                    this._editDataService.sessionNameUpdated(newName, sectionId);
                    return void 0;
                })
            );
    }
}
